import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

interface ProfileData {
  id: string;
  email: string;
  nickname: string;
  avatar_url: string | null;
  created_at: string;
  points: number;
  role: string | null;
}

interface MyPost {
  id: number;
  title: string;
  category: string;
  views: number;
  likes: number;
  created_at: string;
}

interface ProfileStats {
  postCount: number;
  totalLikes: number;
  totalViews: number;
  attendanceStreak: number;
  attendanceTotal: number;
  totalScore: number;
  pointRank: number | null;
  commentCount: number;
  likedPostCount: number;
}

interface MyComment {
  id: number;
  content: string;
  created_at: string;
  post_id: number;
  post_title: string;
}

interface MyLikedPost {
  post_id: number;
  title: string;
  category: string;
  views: number;
  likes: number;
  liked_at: string;
  author_nickname: string;
}

const categoryLabels: Record<string, string> = {
  community: "자유게시판",
  issue: "실시간 이슈",
  scam_report: "먹튀제보",
  safety: "안전정보",
  sports: "스포츠분석",
  betting: "베팅가이드",
};

const POST_WEIGHT = 10;
const ATTENDANCE_WEIGHT = 3;

const roleBadge = (role: string | null) => {
  switch (role) {
    case "admin": return { bg: "bg-red-100", text: "text-red-600", label: "관리자", icon: "ri-shield-check-fill" };
    case "moderator": return { bg: "bg-accent-100", text: "text-accent-700", label: "운영자", icon: "ri-shield-star-fill" };
    case "vip": return { bg: "bg-yellow-100", text: "text-yellow-700", label: "VIP", icon: "ri-vip-crown-fill" };
    default: return null;
  }
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function Profile() {
  const { userId } = useParams<{ userId?: string }>();
  const { user, loading: authLoading } = useAuth();
  const isOwnProfile = !userId || (user && userId === user.id);

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [stats, setStats] = useState<ProfileStats>({
    postCount: 0,
    totalLikes: 0,
    totalViews: 0,
    attendanceStreak: 0,
    attendanceTotal: 0,
    totalScore: 0,
    pointRank: null,
    commentCount: 0,
    likedPostCount: 0,
  });
  const [myPosts, setMyPosts] = useState<MyPost[]>([]);
  const [myComments, setMyComments] = useState<MyComment[]>([]);
  const [likedPosts, setLikedPosts] = useState<MyLikedPost[]>([]);
  const [nickname, setNickname] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  const targetUserId = userId || user?.id;

  const fetchProfile = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from("profiles")
        .select("id, email, nickname, avatar_url, created_at, points, role")
        .eq("id", targetUserId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        setProfile(data);
        setNickname(data.nickname || "");
      } else if (isOwnProfile && user) {
        // Own profile doesn't exist yet, create it
        const { data: newProfile, error: insertError } = await supabase
          .from("profiles")
          .insert({
            id: user.id,
            email: user.email || "",
            nickname: user.user_metadata?.nickname || user.email?.split("@")[0] || "회원",
            points: 0,
            role: "user",
          })
          .select("id, email, nickname, avatar_url, created_at, points, role")
          .maybeSingle();

        if (!insertError && newProfile) {
          setProfile(newProfile);
          setNickname(newProfile.nickname || "");
        }
      }
    } catch {
      setError("프로필을 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [targetUserId, isOwnProfile, user]);

  const fetchStats = useCallback(async () => {
    if (!targetUserId || !profile) return;

    try {
      const [
        { data: postsData },
        { data: attData },
        { count: pointRankCount },
        { count: commentCount },
        { count: likedPostCount },
      ] = await Promise.all([
        supabase.from("posts").select("likes, views").eq("author_id", targetUserId),
        supabase.from("attendances").select("check_date").eq("user_id", targetUserId).order("check_date", { ascending: false }).limit(30),
        supabase.from("profiles").select("id", { count: "exact", head: false }).gt("points", profile.points),
        supabase.from("comments").select("id", { count: "exact", head: true }).eq("author_id", targetUserId),
        supabase.from("post_likes").select("id", { count: "exact", head: true }).eq("user_id", targetUserId),
      ]);

      const postCount = (postsData || []).length;
      const totalLikes = (postsData || []).reduce((s, p) => s + (p.likes || 0), 0);
      const totalViews = (postsData || []).reduce((s, p) => s + (p.views || 0), 0);

      // Attendance streak
      let streak = 0;
      const attRecords = attData || [];
      if (attRecords.length > 0) {
        streak = 1;
        for (let i = 0; i < attRecords.length - 1; i++) {
          const curr = new Date(attRecords[i].check_date);
          const next = new Date(attRecords[i + 1].check_date);
          const diffDays = (curr.getTime() - next.getTime()) / (1000 * 60 * 60 * 24);
          if (Math.round(diffDays) === 1) {
            streak++;
          } else {
            break;
          }
        }
      }

      const totalScore = profile.points + postCount * POST_WEIGHT + attRecords.length * ATTENDANCE_WEIGHT;

      setStats({
        postCount,
        totalLikes,
        totalViews,
        attendanceStreak: streak,
        attendanceTotal: attRecords.length,
        totalScore,
        pointRank: pointRankCount !== null ? pointRankCount + 1 : null,
        commentCount: commentCount ?? 0,
        likedPostCount: likedPostCount ?? 0,
      });
    } catch {
      // stats fail silently
    } finally {
      setStatsLoading(false);
    }
  }, [targetUserId, profile]);

  const fetchMyPosts = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { data, error: postsError } = await supabase
        .from("posts")
        .select("id, title, category, views, likes, created_at")
        .eq("author_id", targetUserId)
        .order("created_at", { ascending: false });

      if (!postsError && data) {
        setMyPosts(data);
      }
    } catch {
      // silently fail
    }
  }, [targetUserId]);

  const fetchMyComments = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { data, error: commentsError } = await supabase
        .from("comments")
        .select("id, content, created_at, post_id")
        .eq("author_id", targetUserId)
        .order("created_at", { ascending: false })
        .limit(5);

      if (!commentsError && data && data.length > 0) {
        // Fetch post titles for each comment
        const postIds = [...new Set(data.map((c) => c.post_id))];
        const { data: postsData } = await supabase
          .from("posts")
          .select("id, title")
          .in("id", postIds);

        const postTitleMap = new Map<number, string>();
        (postsData || []).forEach((p) => postTitleMap.set(p.id, p.title));

        setMyComments(
          data.map((c) => ({
            ...c,
            post_title: postTitleMap.get(c.post_id) || "(삭제된 글)",
          }))
        );
      }
    } catch {
      // silently fail
    }
  }, [targetUserId]);

  const fetchLikedPosts = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { data: likesData, error: likesError } = await supabase
        .from("post_likes")
        .select("post_id, created_at")
        .eq("user_id", targetUserId)
        .order("created_at", { ascending: false })
        .limit(5);

      if (!likesError && likesData && likesData.length > 0) {
        const postIds = [...new Set(likesData.map((l) => l.post_id))];
        const { data: postsData } = await supabase
          .from("posts")
          .select("id, title, category, views, likes, author_id")
          .in("id", postIds);

        const postMap = new Map<number, typeof postsData[0]>();
        (postsData || []).forEach((p) => postMap.set(p.id, p));

        // Fetch author nicknames
        const authorIds = [...new Set((postsData || []).map((p) => p.author_id).filter(Boolean))];
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, nickname")
          .in("id", authorIds);

        const nicknameMap = new Map<string, string>();
        (profilesData || []).forEach((p) => nicknameMap.set(p.id, p.nickname || "익명"));

        setLikedPosts(
          likesData
            .filter((l) => postMap.has(l.post_id))
            .map((l) => {
              const post = postMap.get(l.post_id)!;
              return {
                post_id: post.id,
                title: post.title,
                category: post.category,
                views: post.views || 0,
                likes: post.likes || 0,
                liked_at: l.created_at,
                author_nickname: nicknameMap.get(post.author_id) || "익명",
              };
            })
        );
      }
    } catch {
      // silently fail
    }
  }, [targetUserId]);

  useEffect(() => {
    if (targetUserId) {
      fetchProfile();
    }
  }, [targetUserId, fetchProfile]);

  useEffect(() => {
    if (profile) {
      fetchStats();
      fetchMyPosts();
      fetchMyComments();
      fetchLikedPosts();
    }
  }, [profile, fetchStats, fetchMyPosts, fetchMyComments, fetchLikedPosts]);

  const handleSaveNickname = async () => {
    if (!user) return;
    if (!nickname.trim()) {
      setError("닉네임을 입력해주세요");
      return;
    }

    setError("");
    setSuccess("");
    setSaving(true);

    try {
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ nickname: nickname.trim() })
        .eq("id", user.id);

      if (updateError) {
        setError("닉네임 변경 중 오류가 발생했습니다");
        return;
      }

      setProfile((prev) => (prev ? { ...prev, nickname: nickname.trim() } : null));
      setSuccess("닉네임이 변경되었습니다");
      setEditing(false);

      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("닉네임 변경 중 오류가 발생했습니다");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || (loading && targetUserId)) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-foreground-600">
          <span className="w-4 h-4 flex items-center justify-center animate-spin">
            <i className="ri-loader-4-line" />
          </span>
          <span className="text-sm">로딩 중...</span>
        </div>
      </div>
    );
  }

  if (!isOwnProfile && !profile && !loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
              <i className="ri-user-unfollow-line text-2xl" />
            </span>
          </div>
          <h2 className="text-lg font-bold text-foreground-700 mb-2">존재하지 않는 사용자입니다</h2>
          <p className="text-sm text-foreground-600 mb-4">해당 사용자를 찾을 수 없습니다</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            홈으로 가기
          </Link>
        </div>
      </div>
    );
  }

  if (!user && !profile) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
              <i className="ri-lock-line text-2xl" />
            </span>
          </div>
          <h2 className="text-lg font-bold text-foreground-700 mb-2">로그인이 필요합니다</h2>
          <p className="text-sm text-foreground-600 mb-4">프로필은 로그인 후 이용할 수 있습니다</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            로그인하러 가기
          </Link>
        </div>
      </div>
    );
  }

  const roleBadgeData = roleBadge(profile?.role ?? null);
  const displayName = profile?.nickname || user?.email?.split("@")[0] || "회원";

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[800px] mx-auto px-3 md:px-4 py-6">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-6">
          {!isOwnProfile ? (
            <Link
              to="/"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 border border-background-300 text-foreground-600 hover:text-foreground-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
            </Link>
          ) : (
            <Link
              to="/"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 border border-background-300 text-foreground-600 hover:text-foreground-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
            </Link>
          )}
          <h1 className="text-lg font-bold text-foreground-700">
            {isOwnProfile ? "내 프로필" : `${displayName}님의 프로필`}
          </h1>
        </div>

        {/* Profile Card */}
        <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden mb-6">
          <div className="px-5 md:px-6 py-6 flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <div className="w-20 h-20 rounded-full bg-background-200 border border-primary-500/30 flex items-center justify-center flex-shrink-0">
              <span className="w-10 h-10 flex items-center justify-center text-primary-400">
                <i className="ri-user-fill text-3xl" />
              </span>
            </div>

            <div className="flex-1 text-center sm:text-left">
              {isOwnProfile && editing ? (
                <div className="flex flex-col sm:flex-row items-center gap-2 mb-3">
                  <div className="flex items-center gap-2 bg-background-200 rounded-lg px-4 py-2 border border-background-300 focus-within:border-primary-500/50 transition-colors">
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                      <i className="ri-user-line" />
                    </span>
                    <input
                      type="text"
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      className="bg-transparent text-sm text-foreground-700 outline-none w-40"
                      maxLength={20}
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleSaveNickname}
                      disabled={saving}
                      className="px-3 py-1.5 text-xs bg-primary-500 text-foreground-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {saving ? "저장 중..." : "저장"}
                    </button>
                    <button
                      onClick={() => {
                        setEditing(false);
                        setError("");
                        setNickname(profile?.nickname || "");
                      }}
                      className="px-3 py-1.5 text-xs text-foreground-600 hover:text-foreground-600 bg-background-200 rounded-md hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-3">
                  <h2 className="text-xl font-bold text-foreground-800">
                    {displayName}
                  </h2>
                  {roleBadgeData && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${roleBadgeData.bg} ${roleBadgeData.text}`}>
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className={roleBadgeData.icon} />
                      </span>
                      {roleBadgeData.label}
                    </span>
                  )}
                  {isOwnProfile && (
                    <button
                      onClick={() => {
                        setEditing(true);
                        setError("");
                        setSuccess("");
                      }}
                      className="px-2.5 py-1 text-[10px] text-foreground-600 hover:text-foreground-600 border border-background-300 hover:border-foreground-600 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-3 h-3 flex items-center justify-center inline mr-1">
                        <i className="ri-pencil-line" />
                      </span>
                      수정
                    </button>
                  )}
                </div>
              )}

              {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
              {success && <p className="text-xs text-green-400 mb-2">{success}</p>}

              {isOwnProfile && (
                <p className="text-sm text-foreground-600 flex items-center gap-1.5 justify-center sm:justify-start">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line" />
                  </span>
                  {user?.email}
                </p>
              )}
              <p className="text-xs text-foreground-600 mt-1 flex items-center gap-1.5 justify-center sm:justify-start">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-calendar-line" />
                </span>
                가입일: {profile?.created_at ? formatDate(profile.created_at) : "-"}
              </p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="border-t border-background-300">
            <div className="grid grid-cols-4 sm:grid-cols-8 divide-x divide-background-300 border-b border-background-300">
              <div className="px-3 py-3 text-center">
                <p className="text-base font-bold text-primary-400">{profile?.points?.toLocaleString() ?? 0}</p>
                <p className="text-[10px] text-foreground-600 mt-0.5">포인트</p>
              </div>
              <div className="px-3 py-3 text-center">
                <p className="text-base font-bold text-accent-400">{statsLoading ? "-" : stats.postCount}</p>
                <p className="text-[10px] text-foreground-600 mt-0.5">작성글</p>
              </div>
              <div className="px-3 py-3 text-center">
                <p className="text-base font-bold text-secondary-400">{statsLoading ? "-" : stats.totalLikes}</p>
                <p className="text-[10px] text-foreground-600 mt-0.5">받은 좋아요</p>
              </div>
              <div className="px-3 py-3 text-center">
                <p className="text-base font-bold text-indigo-400">{statsLoading ? "-" : stats.commentCount}</p>
                <p className="text-[10px] text-foreground-600 mt-0.5">작성 댓글</p>
              </div>
              <div className="px-3 py-3 text-center">
                <p className="text-base font-bold text-rose-400">{statsLoading ? "-" : stats.likedPostCount}</p>
                <p className="text-[10px] text-foreground-600 mt-0.5">좋아요한 글</p>
              </div>
              <div className="px-3 py-3 text-center">
                <p className="text-base font-bold text-foreground-700">{statsLoading ? "-" : stats.totalViews.toLocaleString()}</p>
                <p className="text-[10px] text-foreground-600 mt-0.5">총 조회수</p>
              </div>
              <div className="px-3 py-3 text-center">
                <p className="text-base font-bold text-green-500">{statsLoading ? "-" : stats.attendanceStreak}일</p>
                <p className="text-[10px] text-foreground-600 mt-0.5">출석 연속</p>
              </div>
              <div className="px-3 py-3 text-center">
                <p className="text-base font-bold text-yellow-500">
                  {statsLoading || stats.pointRank === null ? "-" : `${stats.pointRank}위`}
                </p>
                <p className="text-[10px] text-foreground-600 mt-0.5">포인트 순위</p>
              </div>
            </div>
          </div>

          {/* Total Score Banner */}
          <div className="px-5 md:px-6 py-3 bg-background-200/50 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-foreground-700">
              <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                <i className="ri-vip-crown-line" />
              </span>
              <span>종합 점수</span>
              <span className="text-[10px] text-foreground-600">
                (포인트 + 글×{POST_WEIGHT} + 출석×{ATTENDANCE_WEIGHT})
              </span>
            </div>
            <span className="text-sm font-black text-primary-500">
              {statsLoading ? "계산 중..." : stats.totalScore.toLocaleString()} 점
            </span>
          </div>
        </div>

        {/* Quick Links (only for own profile) */}
        {isOwnProfile && (
          <div className="flex items-center gap-2 mb-6">
            <Link
              to="/guide/gift"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-background-100 border border-background-300 rounded-xl hover:border-primary-500/50 transition-colors group"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-400 group-hover:text-primary-500 transition-colors">
                <i className="ri-gift-2-line" />
              </span>
              <span className="text-sm font-medium text-foreground-600 group-hover:text-foreground-700 transition-colors whitespace-nowrap">
                기프티콘 교환
              </span>
            </Link>
            <Link
              to="/profile/exchanges"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-background-100 border border-background-300 rounded-xl hover:border-accent-500/50 transition-colors group"
            >
              <span className="w-5 h-5 flex items-center justify-center text-accent-400 group-hover:text-accent-500 transition-colors">
                <i className="ri-exchange-line" />
              </span>
              <span className="text-sm font-medium text-foreground-600 group-hover:text-foreground-700 transition-colors whitespace-nowrap">
                내 교환내역
              </span>
            </Link>
          </div>
        )}

        {/* Recent Comments */}
        {myComments.length > 0 && (
          <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden mb-6">
            <div className="px-5 md:px-6 py-4 border-b border-background-300 flex items-center justify-between">
              <h2 className="text-sm font-bold text-foreground-700 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-indigo-400">
                  <i className="ri-chat-3-line" />
                </span>
                {isOwnProfile ? "내 최근 댓글" : `${displayName}님의 최근 댓글`}
              </h2>
              <Link
                to={isOwnProfile ? "/profile/comments" : `/profile/${targetUserId}/comments`}
                className="flex items-center gap-1 text-[10px] text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap font-medium"
              >
                더보기
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </Link>
            </div>
            <div className="divide-y divide-background-300">
              {myComments.map((comment) => (
                <Link
                  key={comment.id}
                  to={`/post/${comment.post_id}`}
                  className="block px-5 md:px-6 py-3.5 hover:bg-background-200/50 transition-colors group"
                >
                  <p className="text-sm text-foreground-600 group-hover:text-foreground-700 transition-colors line-clamp-2 mb-1.5 leading-relaxed">
                    {comment.content}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line" />
                    </span>
                    <span className="truncate max-w-[240px] text-foreground-600">{comment.post_title}</span>
                    <span className="text-foreground-600 whitespace-nowrap">{formatDate(comment.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Liked Posts */}
        {likedPosts.length > 0 && (
          <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden mb-6">
            <div className="px-5 md:px-6 py-4 border-b border-background-300 flex items-center justify-between">
              <h2 className="text-sm font-bold text-foreground-700 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-red-400">
                  <i className="ri-heart-3-line" />
                </span>
                {isOwnProfile ? "내가 좋아요한 글" : `${displayName}님이 좋아요한 글`}
              </h2>
              <Link
                to={isOwnProfile ? "/profile/likes" : `/profile/${targetUserId}/likes`}
                className="flex items-center gap-1 text-[10px] text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap font-medium"
              >
                더보기
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </Link>
            </div>
            <div className="divide-y divide-background-300">
              {likedPosts.map((item) => (
                <Link
                  key={item.post_id}
                  to={`/post/${item.post_id}`}
                  className="flex items-center gap-4 px-5 md:px-6 py-4 hover:bg-background-200/50 transition-colors group"
                >
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-red-500/15 text-red-400 rounded whitespace-nowrap flex-shrink-0">
                    {categoryLabels[item.category] || item.category}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-foreground-600 group-hover:text-foreground-700 transition-colors truncate block font-medium">
                      {item.title}
                    </span>
                    <span className="text-[10px] text-foreground-600 mt-0.5 block">
                      {item.author_nickname}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-600 flex-shrink-0">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-eye-line" />
                      </span>
                      {item.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-thumb-up-line" />
                      </span>
                      {item.likes}
                    </span>
                    <span className="hidden sm:inline text-foreground-600 whitespace-nowrap">{formatDate(item.liked_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Posts */}
        <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
          <div className="px-5 md:px-6 py-4 border-b border-background-300 flex items-center justify-between">
            <h2 className="text-sm font-bold text-foreground-700 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-primary-400">
                <i className="ri-file-list-3-line" />
              </span>
              {isOwnProfile ? "내가 작성한 글" : `${displayName}님이 작성한 글`}
            </h2>
            <span className="text-[10px] text-foreground-600">{myPosts.length}개</span>
          </div>

          {loading ? (
            <div className="px-5 md:px-6 py-10 flex items-center justify-center">
              <div className="flex items-center gap-2 text-foreground-600">
                <span className="w-4 h-4 flex items-center justify-center animate-spin">
                  <i className="ri-loader-4-line" />
                </span>
                <span className="text-sm">불러오는 중...</span>
              </div>
            </div>
          ) : myPosts.length === 0 ? (
            <div className="px-5 md:px-6 py-10 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
                <span className="w-6 h-6 flex items-center justify-center text-foreground-600">
                  <i className="ri-edit-box-line" />
                </span>
              </div>
              <p className="text-sm text-foreground-600 mb-1">
                {isOwnProfile ? "아직 작성한 글이 없습니다" : "아직 작성한 글이 없습니다"}
              </p>
              {isOwnProfile && (
                <>
                  <p className="text-xs text-foreground-600 mb-3">커뮤니티에 첫 글을 작성해보세요</p>
                  <Link
                    to="/write"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-foreground-50 text-xs font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-pencil-line" />
                    </span>
                    글쓰기
                  </Link>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-background-300">
              {myPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/post/${post.id}`}
                  className="flex items-center gap-4 px-5 md:px-6 py-4 hover:bg-background-200/50 transition-colors group"
                >
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-primary-500/15 text-primary-400 rounded whitespace-nowrap flex-shrink-0">
                    {categoryLabels[post.category] || post.category}
                  </span>
                  <span className="flex-1 text-sm text-foreground-600 group-hover:text-foreground-700 transition-colors truncate min-w-0 font-medium">
                    {post.title}
                  </span>
                  <div className="flex items-center gap-3 text-xs text-foreground-600 flex-shrink-0">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-eye-line" />
                      </span>
                      {post.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-thumb-up-line" />
                      </span>
                      {post.likes}
                    </span>
                    <span className="hidden sm:inline text-foreground-600">{formatDate(post.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}